import { useEffect, useState } from "react";
import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import api from "../services/api";

// Copies another user's (or your own) item into items you own. What it points
// at that you can't change is copied too (a suite's tests, a test's data set);
// the backend decides and the preview says what (CloneService).
const KINDS = {
	test: { path: "tests", label: "test", destination: "folder" },
	suite: { path: "suites", label: "suite", destination: "folder" },
	dataSet: { path: "datasets", label: "data set", destination: "project" },
	project: { path: "projects", label: "project", destination: null },
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function alsoCopied(kind, summary) {
	if (!summary) return [];
	const own = { test: "tests", suite: "suites", dataSet: "dataSets", project: "projects" }[kind];
	const parts = [];
	const count = (key) => (summary[key] || 0) - (key === own ? 1 : 0);
	if (count("folders") > 0) parts.push(plural(count("folders"), "folder"));
	if (count("tests") > 0) parts.push(plural(count("tests"), "test"));
	if (count("dataSets") > 0) parts.push(plural(count("dataSets"), "data set"));
	return parts;
}

export default function CloneDialog({ open, kind, item, onClose, onCloned }) {
	const { enqueueSnackbar } = useSnackbar();
	const config = KINDS[kind];
	const [name, setName] = useState("");
	const [projects, setProjects] = useState([]);
	const [projectId, setProjectId] = useState("");
	const [folders, setFolders] = useState([]);
	const [folderId, setFolderId] = useState("");
	const [summary, setSummary] = useState(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open || !item) return;
		setName(`Copy of ${item.name}`);
		setFolderId("");
		setSummary(null);
		// Where it can go: projects you may change (never another user's)
		setProjectId(item.canModify !== false && item.projectId ? item.projectId : "");
		if (config.destination) {
			api.get("/projects")
				.then((res) => setProjects((res.data || []).filter((p) => p.canModify !== false)))
				.catch(() => setProjects([]));
		}
	}, [open, item, config.destination]);

	useEffect(() => {
		setFolderId("");
		if (!open || config.destination !== "folder" || !projectId) {
			setFolders([]);
			return;
		}
		api.get("/folders", { params: { projectId } })
			.then((res) => setFolders(res.data || []))
			.catch(() => setFolders([]));
	}, [open, projectId, config.destination]);

	const body = () => ({
		name,
		projectId: projectId || null,
		folderId: folderId || null,
	});

	useEffect(() => {
		if (!open || !item) return;
		api.post(`/${config.path}/${item.id}/clone`, body(), { params: { preview: true } })
			.then((res) => setSummary(res.data))
			.catch(() => setSummary(null));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, item, projectId, folderId]);

	const handleClone = async () => {
		setSaving(true);
		try {
			const res = await api.post(`/${config.path}/${item.id}/clone`, body());
			enqueueSnackbar(`Cloned into "${res.data.name}" — it's yours to edit`, { variant: "success" });
			onCloned?.(res.data);
			onClose();
		} catch (err) {
			enqueueSnackbar(err.response?.data?.message || "Clone failed", { variant: "error" });
		} finally {
			setSaving(false);
		}
	};

	const extra = alsoCopied(kind, summary);

	return (
		<Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle fontWeight={700}>Clone {config?.label}</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						You'll own the copy and can edit it. Run history isn't copied.
					</Typography>
					<TextField
						label="Name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						fullWidth
						autoFocus
					/>
					{config?.destination && (
						<FormControl fullWidth>
							<InputLabel id="clone-project-label">Project</InputLabel>
							<Select
								labelId="clone-project-label"
								label="Project"
								value={projectId}
								onChange={(e) => setProjectId(e.target.value)}
							>
								<MenuItem value="">
									<em>No project</em>
								</MenuItem>
								{projects.map((p) => (
									<MenuItem key={p.id} value={p.id}>
										{p.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
					{config?.destination === "folder" && projectId && folders.length > 0 && (
						<FormControl fullWidth>
							<InputLabel id="clone-folder-label">Folder</InputLabel>
							<Select
								labelId="clone-folder-label"
								label="Folder"
								value={folderId}
								onChange={(e) => setFolderId(e.target.value)}
							>
								<MenuItem value="">
									<em>No folder</em>
								</MenuItem>
								{folders.map((f) => (
									<MenuItem key={f.id} value={f.id}>
										{f.path || f.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
					{extra.length > 0 && (
						<Alert severity="info">
							{kind === "project"
								? `Also copies its ${extra.join(", ")}.`
								: `Also copies ${extra.join(", ")} it uses that you don't own.`}
						</Alert>
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onClose} disabled={saving}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleClone}
					disabled={saving || !name.trim()}
					startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <ContentCopy />}
				>
					Clone
				</Button>
			</DialogActions>
		</Dialog>
	);
}
